// useProductoFormXL - Sprint 44 Fase 3 Vista 3.
// Hook del form de producto con:
//   - Carga por :id o blank para create.
//   - Save (POST/PUT) con el SOLO los 12 campos que el backend mapea
//     (codigo, nombre, descripcion, categoria, unidad_medida, peso_kg,
//      volumen_m3, marca, sku, precio_venta, costo_ultimo, estado).
//   - Upload/delete imagen reusa fetch multipart con Authorization header,
//     mismo patron que legacy (no hay helper multipart en apiClient).
//   - Toggle Activar/Inactivar: usa PUT con estado='activo'/'inactivo'
//     (no se introduce endpoint nuevo).
//   - msg auto-dismiss 4s.
//   - Categorias carga separado para datalist del tab Basicos.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient, { API_BASE } from '../../../api/apiClient';
import { asString, blankForm, TAB_BY_FIELD } from '../utils';

const MSG_AUTO_DISMISS_MS = 4000;

export default function useProductoFormXL(productoId) {
  const navigate = useNavigate();
  const isEdit = Boolean(productoId);

  const [form, setForm]         = useState(blankForm);
  const [errors, setErrors]     = useState({});
  const [msg, setMsg]           = useState(null);
  const [loading, setLoading]   = useState(isEdit);
  const [saving, setSaving]     = useState(false);
  const [acting, setActing]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab]           = useState('basicos');
  const [categorias, setCategorias] = useState([]);

  const msgTimer = useRef(null);
  const pushMsg = useCallback((kind, text) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    setMsg({ kind, text });
    msgTimer.current = setTimeout(() => setMsg(null), MSG_AUTO_DISMISS_MS);
  }, []);
  useEffect(() => () => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
  }, []);

  const applyDto = useCallback((d) => ({
    codigo: asString(d.codigo),
    nombre: asString(d.nombre),
    descripcion: asString(d.descripcion),
    categoria: asString(d.categoria),
    unidad_medida: d.unidad_medida || 'UND',
    peso_kg: asString(d.peso_kg),
    volumen_m3: asString(d.volumen_m3),
    marca: asString(d.marca),
    sku: asString(d.sku),
    precio_venta: d.precio_venta === null || d.precio_venta === undefined ? '' : d.precio_venta,
    costo_ultimo: d.costo_ultimo === null || d.costo_ultimo === undefined ? '' : d.costo_ultimo,
    costo_real: d.costo_real,
    costo_promedio: d.costo_promedio,
    estado: d.is_active ? 'activo' : 'inactivo',
    is_active: !!d.is_active,
    imagen_url: d.imagen_url || null,
    imagen_thumbnail_url: d.imagen_thumbnail_url || null,
    stock: d.stock,
    created_at: d.created_at || null,
    updated_at: d.updated_at || null,
  }), []);

  useEffect(() => {
    let alive = true;
    apiClient.get('/productos/categorias')
      .then((r) => { if (alive) setCategorias((r && r.data && r.data.items) || []); })
      .catch(() => { if (alive) setCategorias([]); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!productoId) { setForm(blankForm); setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    apiClient.get('/productos/' + productoId)
      .then((res) => { if (alive) setForm(applyDto((res && res.data) || {})); })
      .catch((e) => { if (alive) pushMsg('err', (e && e.message) || 'Error al cargar'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [productoId, applyDto, pushMsg]);

  const set = useCallback((k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
  }, []);

  const buildPayload = useCallback(() => ({
    codigo:        (form.codigo || '').trim(),
    nombre:        (form.nombre || '').trim(),
    descripcion:   (form.descripcion || '').trim() || null,
    categoria:     (form.categoria || '').trim() || null,
    unidad_medida: form.unidad_medida || 'UND',
    peso_kg:       form.peso_kg     === '' || form.peso_kg     == null ? null : Number(form.peso_kg),
    volumen_m3:    form.volumen_m3  === '' || form.volumen_m3  == null ? null : Number(form.volumen_m3),
    marca:         (form.marca || '').trim() || null,
    sku:           (form.sku || '').trim() || null,
    precio_venta:  form.precio_venta === '' || form.precio_venta == null ? null : Number(form.precio_venta),
    costo_ultimo:  form.costo_ultimo === '' || form.costo_ultimo == null ? null : Number(form.costo_ultimo),
    estado:        form.estado || 'activo',
  }), [form]);

  const onSave = useCallback(async () => {
    setErrors({});
    setSaving(true);
    try {
      const payload = buildPayload();
      let res;
      if (isEdit) {
        res = await apiClient.put('/productos/' + productoId, payload);
      } else {
        res = await apiClient.post('/productos/', payload);
      }
      const d = (res && res.data) || {};
      if (isEdit) {
        setForm(applyDto(d));
        pushMsg('ok', 'Producto actualizado');
      } else {
        pushMsg('ok', 'Producto creado');
        if (d && d.id) navigate('/productos/' + d.id + '/edit', { replace: true });
      }
    } catch (e) {
      const apiErrs = (e && e.data && e.data.errors) || null;
      if (apiErrs) {
        setErrors(apiErrs);
        const firstField = Object.keys(apiErrs)[0];
        const jumpTo = TAB_BY_FIELD[firstField];
        if (jumpTo) setTab(jumpTo);
      }
      const m = (e && e.data && e.data.message) || (e && e.message) || 'Error al guardar';
      pushMsg('err', m);
    } finally {
      setSaving(false);
    }
  }, [isEdit, productoId, buildPayload, applyDto, navigate, pushMsg]);

  const onToggleActivo = useCallback(async () => {
    if (!productoId) return;
    setActing(true);
    try {
      const nextEstado = form.is_active ? 'inactivo' : 'activo';
      const res = await apiClient.put('/productos/' + productoId, { estado: nextEstado });
      const d = (res && res.data) || {};
      setForm(applyDto(d));
      pushMsg('ok', d.is_active ? 'Producto activado' : 'Producto inactivado');
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error al cambiar estado');
    } finally {
      setActing(false);
    }
  }, [productoId, form.is_active, applyDto, pushMsg]);

  const onUploadImage = useCallback(async (file) => {
    if (!productoId || !file) return;
    if (file.size > 2 * 1024 * 1024) {
      pushMsg('err', 'La imagen supera 2 MB.');
      return;
    }
    const okMime = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!okMime.includes(file.type)) {
      pushMsg('err', 'Formato no permitido. Usa jpg, png o webp.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = apiClient.getAccessToken();
      const res = await fetch(API_BASE + '/productos/' + productoId + '/imagen', {
        method: 'POST',
        headers: token ? { Authorization: 'Bearer ' + token } : {},
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) {
        pushMsg('err', (json && json.message) || 'No se pudo subir');
        return;
      }
      setForm((f) => ({
        ...f,
        imagen_url: json && json.data && json.data.imagen_url,
        imagen_thumbnail_url: json && json.data && json.data.imagen_thumbnail_url,
      }));
      pushMsg('ok', 'Imagen subida');
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error de red');
    } finally {
      setUploading(false);
    }
  }, [productoId, pushMsg]);

  const onDeleteImage = useCallback(async () => {
    if (!productoId) return;
    try {
      await apiClient.delete('/productos/' + productoId + '/imagen');
      setForm((f) => ({ ...f, imagen_url: null, imagen_thumbnail_url: null }));
      pushMsg('ok', 'Imagen eliminada');
    } catch (e) {
      pushMsg('err', (e && e.message) || 'No se pudo eliminar');
    }
  }, [productoId, pushMsg]);

  return {
    isEdit, navigate, tab, setTab,
    form, set, errors,
    loading, saving, acting, uploading,
    msg, pushMsg, clearMsg: () => setMsg(null),
    categorias,
    onSave, onToggleActivo, onUploadImage, onDeleteImage,
  };
}
